import React, { useEffect, useState } from "react";
import { RootState } from "../../reducers/store";
import { useDispatch, useSelector } from "react-redux";
import Title from "../general/title";
import SearchCompaniesComponent from "../searchCompanies";
import SearchProductsComponent from "../searchProducts";
import {
    getCompanyProducts,
    buyCompanyProduct,
    buyCompanyAggregatedProducts,
} from "../../actions/products";
import BuyAggregatedProductsModal from "../general/modals/buyAggregatedProducts";
import BuyProductModal from "../general/modals/buyProduct";
import Notification from "../vapor/notification";
import Item from "../vapor/item";
import { clearCompanies, getCompanies } from "../../actions/company";
import { Button, InputAdornment, TextField } from "@vapor/react-material";
import { getUserPackage } from "../../actions/userPackage";
import Spinner from "../vapor/spinner";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { AggregatedProductInterface } from "../../interfaces/products";

declare const window: any;

interface BuyModalInterface {
    name?: string;
    product?: string;
    price?: string;
}

interface MainComponentProps {
    step: number;
    setStep: (val: number) => void;
    expanded?: boolean;
    setShowNotification: (val: any) => void;
    showNotification?: any;
}

const MainComponent = (props: MainComponentProps) => {
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [companySelected, setCompanySelected] = useState(null);
    const [buyAggregatedProductsModal, setBuyAggregatedProductsModal] =
        useState<AggregatedProductInterface | null>(null);
    const [buyModal, setBuyModal] = useState<BuyModalInterface>({});
    const [showErrorNotification, setShowErrorNotification] = useState(false);
    const [
        showErrorAggregatedProductsNotification,
        setShowErrorAggregatedProductsNotification,
    ] = useState(false);
    const [balanceType, setBalanceType] = useState<any>("none");
    const [balanceYear, setBalanceYear] = useState<any>("none");

    const navigate = useNavigate();

    const search = useSelector((state: RootState) => state.search);
    const userPackage = useSelector((state: RootState) => state.userPackage);
    const modebisconfig = useSelector(
        (state: RootState) => state.modebisconfig,
    );

    const dispatch: any = useDispatch();

    useEffect(() => {
        window?.aptrinsic?.("track", "business-information:DASHBOARD");
    }, []);

    useEffect(() => {
        if (
            props.step === 2 &&
            !search.products.isGettingCompanyProducts &&
            companySelected
        ) {
            dispatch(getCompanyProducts(companySelected));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.step, companySelected]);

    const onBuyAggregatedProducts = (
        aggregatedProduct: AggregatedProductInterface,
    ) => {
        setBuyAggregatedProductsModal(aggregatedProduct);
    };

    const onBuyAggregatedProductsConfirm = () => {
        if (companySelected && buyAggregatedProductsModal) {
            dispatch(
                buyCompanyAggregatedProducts(
                    companySelected,
                    buyAggregatedProductsModal.products,
                ),
            )
                .then(() => {
                    setBuyAggregatedProductsModal(null);
                    window?.aptrinsic?.(
                        "track",
                        "business-information:BUY-AGGREGATED-PRODUCT",
                    );
                    props.setShowNotification(true);
                    dispatch(getUserPackage());
                    navigate("/documenti");
                })
                .catch(() => {
                    setBuyAggregatedProductsModal(null);
                    setShowErrorAggregatedProductsNotification(true);
                });
        }
    };

    const onBuyProduct = (product: any) => {
        setBuyModal(product);
        setBalanceType("none");
    };

    const onBuyConfirm = () => {
        if (companySelected && buyModal && buyModal.product) {
            dispatch(
                buyCompanyProduct(
                    companySelected,
                    buyModal.product,
                    balanceType,
                    balanceYear,
                ),
            )
                .then(() => {
                    setBuyModal({});
                    window?.aptrinsic?.(
                        "track",
                        "business-information:BUY-PRODUCT",
                    );
                    props.setShowNotification(true);
                    dispatch(getUserPackage());
                    navigate("/documenti");
                })
                .catch(() => {
                    setBuyModal({});
                    setShowErrorNotification(true);
                });
        }
    };

    const onBuyConfirmAtto = (kDoc: any) => {
        if (companySelected && buyModal && buyModal.product) {
            dispatch(
                buyCompanyProduct(
                    companySelected,
                    buyModal.product,
                    null,
                    null,
                    null,
                    null,
                    kDoc,
                ),
            )
                .then(() => {
                    setBuyModal({});
                    window?.aptrinsic?.(
                        "track",
                        "business-information:BUY-PRODUCT",
                    );
                    props.setShowNotification(true);
                    dispatch(getUserPackage());
                    navigate("/documenti");
                })
                .catch(() => {
                    setBuyModal({});
                    setShowErrorNotification(true);
                });
        }
    };

    const searchCompanies = (val?: string) => {
        if (companySearchTerm.length > 2 || val) {
            window?.aptrinsic?.("track", "business-information:SEARCH-COMPANY");
            dispatch(getCompanies(val || companySearchTerm));
        }
        if (companySearchTerm.length === 0) {
            props.setStep(1);
            dispatch(clearCompanies());
        }
    };

    return (
        <>
            {showErrorNotification && (
                <Notification
                    // max ms value allowed (see at https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values)
                    duration={6000}
                    title={<FormattedMessage id="feedbacks.buy.errorTitle" />}
                    variant={"error"}
                    onClose={() => {
                        setShowErrorNotification(false);
                    }}
                >
                    <FormattedMessage id={"feedbacks.buy.errorMessage"} />
                </Notification>
            )}
            {showErrorAggregatedProductsNotification && (
                <Notification
                    duration={6000}
                    title={<FormattedMessage id="feedbacks.buy.errorTitle" />}
                    variant={"error"}
                    onClose={() => {
                        setShowErrorAggregatedProductsNotification(false);
                    }}
                >
                    <FormattedMessage
                        id={"feedbacks.buy.aggregatedProductsErrorMessage"}
                    />
                </Notification>
            )}
            {Object.keys(buyModal).length > 0 && (
                <BuyProductModal
                    onConfirm={() => onBuyConfirm()}
                    onConfirmAtto={(kDoc: any) => onBuyConfirmAtto(kDoc)}
                    onCancel={() => setBuyModal({})}
                    product={buyModal}
                    balanceType={balanceType}
                    setBalanceType={setBalanceType}
                    balanceYear={balanceYear}
                    setBalanceYear={setBalanceYear}
                    companySelected={companySelected}
                />
            )}
            {buyAggregatedProductsModal && (
                <BuyAggregatedProductsModal
                    onConfirm={onBuyAggregatedProductsConfirm}
                    onCancel={() => setBuyAggregatedProductsModal(null)}
                    aggregatedProduct={buyAggregatedProductsModal}
                />
            )}
            {!userPackage.isLoadingPackage && (
                <Title
                    content={
                        <Item
                            disabled={modebisconfig.readOnly}
                            label={
                                <FormattedMessage id="general.searchCompany" />
                            }
                        >
                            <TextField
                                id="bis-company-search"
                                type="search"
                                readOnly={modebisconfig.readOnly}
                                disabled={modebisconfig.readOnly}
                                autoComplete="off"
                                value={companySearchTerm}
                                onChange={(e: any) => {
                                    if (
                                        e.nativeEvent instanceof InputEvent ===
                                        false
                                    ) {
                                        props.setStep(1);
                                        dispatch(clearCompanies());
                                    }
                                    setCompanySearchTerm(e.target.value);
                                }}
                                onKeyPress={(e: any) => {
                                    if (e.key === "Enter") {
                                        searchCompanies();
                                        if (props.step !== 1) {
                                            props.setStep(1);
                                        }
                                    }
                                }}
                                placeholder="Inserisci Ragione Sociale, Codice Fiscale o Partita IVA"
                                InputProps={{
                                    autoComplete: "off",
                                    endAdornment: (
                                        <InputAdornment
                                            style={{ marginRight: 0 }}
                                            position="end"
                                        >
                                            <Button
                                                disabled={
                                                    modebisconfig.readOnly
                                                }
                                                onClick={() => {
                                                    searchCompanies();
                                                    if (props.step !== 1) {
                                                        props.setStep(1);
                                                    }
                                                }}
                                                variant="contained"
                                            >
                                                <FormattedMessage id="actions.search" />
                                            </Button>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Item>
                    }
                    showCredits
                />
            )}
            {props.step === 1 && !userPackage.isLoadingPackage && (
                <SearchCompaniesComponent
                    searchTerm={companySearchTerm}
                    setSearchTerm={setCompanySearchTerm}
                    onSelect={(value: any) => {
                        props.setStep(2);
                        setCompanySelected(value);
                    }}
                    expanded={props.expanded}
                />
            )}
            {props.step === 2 &&
                !userPackage.isLoadingPackage &&
                companySelected && (
                    <SearchProductsComponent
                        onBuyAggregatedProducts={(
                            item: AggregatedProductInterface,
                        ) => {
                            onBuyAggregatedProducts(item);
                        }}
                        onBuyProduct={(item: any) => {
                            onBuyProduct(item);
                        }}
                        onNextStep={() => {
                            props.setStep(3);
                        }}
                        onClose={() => {
                            setBalanceType("none");
                            props.setStep(1);
                        }}
                        expanded={props.expanded}
                    />
                )}
            {props.step === 2 && userPackage.isLoadingPackage && (
                <div className="searchResultsSpinner" style={{ marginTop: 32 }}>
                    <Spinner
                        message={
                            <FormattedMessage id="general.updateCredits" />
                        }
                    />
                </div>
            )}
        </>
    );
};

export default MainComponent;
